import { inject, TemplateRef, Service } from '@angular/core';
import { DataAccessIstatService } from '@h5s/data-access-istat';
import { DialogService, ModalService, StatusLabelTag, ToastService } from '@h5s/design-system';
import {
  AstatDailyLocalTaxInfo,
  AstatDailyLocalTaxInfoRequest,
  AstatDailyMovement,
  CheckAstatDailyGuestsInClosurePeriodRequest,
  CityTaxCheckInformation,
  Customer,
  CustomerInfoWithoutInvoicedCityTax,
  GetCityTaxCheckInformationRequest,
  GetIstatCustomerRequest,
  IstatCustomer,
  IstatCustomerMode,
  IstatGenerationParameters,
  IstatMode,
  IstatSendStatus,
  IstatStatus,
  IstatTypeEnum,
  OwnerIstatProfile,
  PurchasedStayToCustomer,
  ResourceValidationResult,
  SendAstatDailyFacilityClosureRequest,
  SendAstatDailyIstatClosureRequest,
  SendIstatRequest,
  UpdateCustomer,
} from '@h5s/entities';
import { AppErrorBuilder, Severity, showErrorAsToast } from '@h5s/util-error';
import { LazyComponent } from '@h5s/util-object';
import { ActionType, ModalComponent, OverlayResult } from '@h5s/util-overlay';
import { DateTime } from 'luxon';
import { catchError, EMPTY, filter, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { ClosedStructureModalResult } from './feature-istat/components/istat-filter/components/closed-structure-modal/models/closed-structure-modal-result';
import { ConsolidateIstatDataModalResult } from './feature-istat/components/istat-filter/components/consolidate-istat-data-modal/models/consolidate-istat-data-modal-result';
import { TableIstatCustomer } from './feature-istat/components/istat-table/models/table-istat-customer';
import { SendStatusTooltip, StatusHasError, TextSendStatus } from './feature-istat/model/send-status-tooltip';
import { FeatureIstatStore } from './feature-istat/store/feature-istat.store';
import { FEATURE_ISTAT_TRANSLATIONS } from './feature-istat/translations/feature-istat.translations';

@Service()
export class FeatureIstatService {
  private readonly dataAccessIstatService = inject(DataAccessIstatService);
  private readonly modalService = inject(ModalService);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastService);

  /** Traduzioni */
  public readonly translation = FEATURE_ISTAT_TRANSLATIONS;

  public research = new Subject<void>();
  public selectReadyIstatCustomer = new Subject<void>();
  public ownerIstatProfile: OwnerIstatProfile[] | null = null;

  /**
   * Restituisce un Observable che emette la lista dei profili Istat .
   */
  public getOwnerIstatProfileList(): Observable<OwnerIstatProfile[]> {
    return this.ownerIstatProfile
      ? of(this.ownerIstatProfile)
      : this.dataAccessIstatService.getOwnerIstatProfile().pipe(tap((oip) => (this.ownerIstatProfile = oip)));
  }

  /**
   * Controllo se esistono profili Istat.
   */
  public hasOwnerIstatProfile(): Observable<boolean> {
    return this.getOwnerIstatProfileList().pipe(map((oip) => oip.length > 0));
  }

  /**
   * Controlla se esistono profili Istat e mostra un alert se non ne esistono.
   */
  public checkOwnerIstatProfile(): Observable<boolean> {
    return this.hasOwnerIstatProfile().pipe(
      tap((hasProfiles) => {
        if (!hasProfiles) {
          this.dialogService.showAlert({ content: this.translation.alertNoIstatProfile });
        }
      }),
    );
  }

  /**
   * Restituisce un Observable che emette la lista dei clienti Istat filtrati in base ai parametri di ricerca.
   */
  public getFilteredIstatCustomers(request: GetIstatCustomerRequest): Observable<IstatCustomer[]> {
    return this.dataAccessIstatService.getIstatCustomer(request);
  }

  /**
   * Restituisce un Observable che emette I parametri di generazione.
   */
  public getIstatGenerationParametersByOwnerIstatProfileId(id: number): Observable<IstatGenerationParameters> {
    return this.dataAccessIstatService.getIstatGenerationParameters(id);
  }

  /**
   * Restituisce un Observable che emette le informazioni per il controllo della tassa di soggiorno.
   */
  public getCityTaxCheckInformation(request: GetCityTaxCheckInformationRequest): Observable<CityTaxCheckInformation> {
    return this.dataAccessIstatService.getCityTaxCheckInformation(request);
  }

  /**
   * Invio Istat online
   */
  public sendIstatOnline(istatSendRequest: SendIstatRequest): Observable<string> {
    return this.dataAccessIstatService.sendIstatOnline(istatSendRequest).pipe(
      tap(() => {
        this.toastService.showSuccess(this.translation.toastSendIstatOnlineOk);
      }),
      catchError((error) => {
        throw new AppErrorBuilder(`[FeatureIstatService] Error sending ISTAT data online`, Severity.ERROR)
          .addCause(error)
          .build();
      }),
      showErrorAsToast(this.translation.toastSendIstatOnlineError),
    );
  }

  /**
   * Apre modale per "Parametri di Generazione"
   */
  public openGenerationParamsModal(store: unknown): Observable<OverlayResult<undefined>> {
    const lazyComponent = (): Promise<LazyComponent<ModalComponent>> =>
      import('./feature-istat/components/generation-params-modal/generation-params-modal.component').then((x) => ({
        component: x.GenerationParamsModalComponent,
      }));
    return this.modalService.show({
      loadComponent: lazyComponent,
      title: this.translation.generationParamsTitle,
      confirmButtonText: this.translation.confirmButton,
      providers: [{ provide: FeatureIstatStore, useValue: store }],
    });
  }

  /**
   * Record per IstatSendStatus che contiene le informazioni da visualizzare nella tabella.
   */
  private readonly istatSendStatusConfig: Record<IstatSendStatus, { text: string; status: StatusLabelTag }> = {
    [IstatSendStatus.IstatSendNotSent]: {
      text: this.translation.status.send.notSent,
      status: 'light-dark',
    },
    [IstatSendStatus.IstatSendNotValid]: {
      text: this.translation.status.send.sendError,
      status: 'danger',
    },
    [IstatSendStatus.IstatSendStatusCityTax]: {
      text: this.translation.status.type.cityTax,
      status: 'light-dark',
    },
    [IstatSendStatus.IstatSendValid]: {
      text: this.translation.status.send.sent,
      status: 'confirm',
    },
  };

  public getIstatSendStatusConfig(value: IstatSendStatus): { text: string; status: StatusLabelTag } | null {
    return this.istatSendStatusConfig[value] ?? null;
  }

  public validateCustomerForIstat(customer: UpdateCustomer): Observable<ResourceValidationResult<Customer>[]> {
    return this.dataAccessIstatService.validateCustomerForIstat(customer);
  }

  /**
   * Record per IstatCustomerMode che contiene le informazioni da visualizzare nella tabella nella colonna in/out.
   */
  private readonly istatCustomerModeConfig: Readonly<Record<IstatCustomerMode, string>> = {
    [IstatCustomerMode.IstatCustomerModeIn]: this.translation.istatCustomerModeIn,
    [IstatCustomerMode.IstatCustomerModeOut]: this.translation.istatCustomerModeOut,
    [IstatCustomerMode.IstatCustomerModeStill]: this.translation.istatCustomerModeStill,
    [IstatCustomerMode.IstatCustomerModeTax]: this.translation.istatCustomerModeTax,
  };

  public getIstatCustomerMode(mode: IstatCustomerMode): string {
    return this.istatCustomerModeConfig[mode] ?? this.translation.istatCustomerUnknownMode;
  }

  /**
   * Restituisce il tooltip da visualizzare nella colonna "Stato" della tabella Istat.
   * @param istatCustomer Il cliente Istat per cui ottenere il tooltip.
   */
  public getStatusTooltip(istatCustomer: TableIstatCustomer): SendStatusTooltip | undefined {
    const purchasedStayToCustomer = istatCustomer.purchasedStayToCustomer;

    if (!purchasedStayToCustomer) {
      return;
    }

    const istatMode = istatCustomer.istatMode;

    const tooltip: SendStatusTooltip = {
      customerSendStatus: this.getIStatTooltipFromPurchasedStayToCustomer(
        purchasedStayToCustomer,
        istatCustomer.istatMode,
      ),
      relativesSendStatus: [],
    };

    // Inserisce gli status dei relatives, se presenti
    const relatives = istatCustomer.relativePurchasedStayToCustomers;
    if (relatives && relatives.length > 0) {
      tooltip.relativesSendStatus = relatives.map((relativeIstatCustomer) =>
        this.getIStatTooltipFromPurchasedStayToCustomer(relativeIstatCustomer, istatMode),
      );
    }
    return tooltip;
  }

  /**
   * Ottiene il testo del tooltip associato ad ogni PurchasedStayToCustomer
   */
  private getIStatTooltipFromPurchasedStayToCustomer(
    purchasedStayToCustomer: PurchasedStayToCustomer,
    mode: IstatMode,
  ): TextSendStatus {
    const customer = purchasedStayToCustomer.customer;
    const istatStatus = purchasedStayToCustomer.istatStatus;

    // Valuta se ci sono errori
    const hasError =
      !!IstatStatus.IstatStatusCheckInSentError ||
      !!IstatStatus.IstatStatusCheckOutSentError ||
      !!IstatStatus.IstatStatusStillSentError;

    // Costruisci la descrizione di status
    const statusDescription = this.getErrorStatusMessage(mode, istatStatus);

    // Prepara l’oggetto StatusHasError o null
    let statusHasError: StatusHasError | null = null;
    if (hasError) {
      statusHasError = {
        ErrorCode: purchasedStayToCustomer.istatErrorCode,
        ErrorDescription: purchasedStayToCustomer.istatErrorDescription,
        ErrorDetail: purchasedStayToCustomer.istatErrorDetail,
      };
    }

    // Crea e ritorna l’oggetto TextSendStatus
    return {
      name: `${customer.surname} ${customer.name}`,
      statusDescription,
      statusHasError,
    };
  }

  /** Ottiene il messaggio di errorStatus */
  private getErrorStatusMessage(mode: IstatMode, istatStatus: IstatStatus | null): string {
    switch (mode) {
      case IstatMode.IstatModeNew:
        return this.getCheckInMessage(istatStatus);

      case IstatMode.IstatModeEdit:
        return this.getCheckOutMessage(istatStatus);

      case IstatMode.IstatModeStill:
        return this.getStillMessage(istatStatus);

      default:
        return this.translation.tooltip.status.unknown;
    }
  }

  private getCheckInMessage(istatStatus: IstatStatus | null): string {
    if (istatStatus === null) {
      return this.translation.tooltip.status.checkIn.notSent;
    }

    if (istatStatus === IstatStatus.IstatStatusCheckInSentError) {
      return this.translation.tooltip.status.checkIn.sentError;
    }

    return this.translation.tooltip.status.checkIn.sentOk;
  }

  private getCheckOutMessage(istatStatus: IstatStatus | null): string {
    if (
      istatStatus === null ||
      istatStatus === IstatStatus.IstatStatusCheckInSentOk ||
      istatStatus === IstatStatus.IstatStatusStillSentOk
    ) {
      return this.translation.tooltip.status.checkOut.notSent;
    }

    if (istatStatus === IstatStatus.IstatStatusCheckInSentError) {
      return this.translation.tooltip.status.checkIn.sentError;
    }

    if (istatStatus === IstatStatus.IstatStatusStillSentError) {
      return this.translation.tooltip.status.still.sentError;
    }

    if (istatStatus === IstatStatus.IstatStatusCheckOutSentError) {
      return this.translation.tooltip.status.checkOut.sentError;
    }

    return this.translation.tooltip.status.checkOut.sentOk;
  }

  private getStillMessage(istatStatus: IstatStatus | null): string {
    if (istatStatus === null || istatStatus === IstatStatus.IstatStatusCheckInSentOk) {
      return this.translation.tooltip.status.still.notSent;
    }

    if (istatStatus === IstatStatus.IstatStatusCheckInSentError) {
      return this.translation.tooltip.status.checkIn.sentError;
    }

    if (istatStatus === IstatStatus.IstatStatusStillSentError) {
      return this.translation.tooltip.status.still.sentError;
    }

    return this.translation.tooltip.status.still.sentOk;
  }

  /**
   * Apre modale per impostare i giorni di chiusura della struttura
   */
  private openClosedStructureModal(): Observable<OverlayResult<ClosedStructureModalResult>> {
    const lazyComponent = (): Promise<LazyComponent<ModalComponent>> =>
      import('./feature-istat/components/istat-filter/components/closed-structure-modal/closed-structure-modal.component').then(
        (x) => ({
          component: x.ClosedStructureModalComponent,
        }),
      );
    return this.modalService.show({
      loadComponent: lazyComponent,
      title: this.translation.reportClosedStructureTitle,
      confirmButtonText: this.translation.confirmButton,
    });
  }

  /**
   * Apre il modale per segnalare le date di chiusura della struttura
   */
  public openReportClosedStructureModal(profile: OwnerIstatProfile): Observable<string | null> {
    return this.openClosedStructureModal().pipe(
      filter(
        (result): result is OverlayResult<ClosedStructureModalResult> & { data: ClosedStructureModalResult } =>
          result.actionType === ActionType.Confirm && !!result.data,
      ),
      switchMap((result) => {
        // Il controllo degli ospiti già registrati per i giorni di chiusura lo facciamo solo per l'ASTAT
        if (profile.istatType.type === IstatTypeEnum.AstatDaily) {
          const params = new CheckAstatDailyGuestsInClosurePeriodRequest();
          params.closureBeginDate = result.data.beginDate;
          params.closureEndDate = result.data.endDate;
          params.ownerIstatId = profile.id;

          return this.dataAccessIstatService.checkAstatDailyGuestsInClosurePeriod(params).pipe(
            map((checkResult) => ({
              beginDate: result.data.beginDate,
              endDate: result.data.endDate,
              checkResult,
            })),
          );
        }

        return of({
          beginDate: result.data.beginDate,
          endDate: result.data.endDate,
          checkResult: {
            zeroArrivesAndPresences: true,
            movements: [],
          },
        });
      }),
      switchMap(({ beginDate, endDate, checkResult }) => {
        // Mostra il dialog di errore per ospiti dichiarati nei giorni di chiusura solo nel caso dell'ASTAT
        if (!checkResult.zeroArrivesAndPresences) {
          return this.openErrorGuestsInClosureDialog(checkResult.movements);
        }

        const params = new SendAstatDailyFacilityClosureRequest();
        params.closureBeginDate = beginDate;
        params.closureEndDate = endDate;
        params.ownerIstatId = profile.id;

        // Invia i giorni di chiusura
        return this.dataAccessIstatService.sendAstatDailyFacilityClosure(params);
      }),
      catchError((error) => {
        throw new AppErrorBuilder(`[FeatureIstatService] Error sending astat daily facility closure`, Severity.ERROR)
          .addCause(error)
          .build();
      }),
    );
  }

  /**
   * Apre il dialog per mostrare il messaggio di errore
   */
  private openErrorGuestsInClosureDialog(movements: AstatDailyMovement[]): Observable<null> {
    return this.dialogService
      .showDanger({
        title: this.translation.titleWarning,
        content: this.buildGuestsInClosurePeriodsDialogMessages(movements),
        confirmButtonText: $localize`:@@common.button.understood:Ho capito`,
        cancelButtonText: undefined,
      })
      .pipe(map(() => null));
  }

  /**
   * Restituisce il messaggio da mostrare nel dialog
   */
  private buildGuestsInClosurePeriodsDialogMessages(movements: AstatDailyMovement[]): string {
    let message = /* HTML */ `<div>${this.translation.dialogBuildGuestsInClosurePeriods.warningMessage}</div>`;

    for (const movement of movements) {
      const status = movement.open
        ? this.translation.dialogBuildGuestsInClosurePeriods.facilityStatus.open
        : this.translation.dialogBuildGuestsInClosurePeriods.facilityStatus.closed;
      message += /* HTML */ `<div>
        ${movement.date}: ${status}, ${this.translation.dialogBuildGuestsInClosurePeriods.totals.arrivals}:
        ${movement.totalArrivals}, ${this.translation.dialogBuildGuestsInClosurePeriods.totals.departures}:
        ${movement.totalDepartures}, ${this.translation.dialogBuildGuestsInClosurePeriods.totals.guestsPresent}:
        ${movement.totalGuestsPresent}, ${this.translation.dialogBuildGuestsInClosurePeriods.totals.occupiedUnits}:
        ${movement.occupiedUnits}
      </div>`;
    }

    message += /* HTML */ `<div>${this.translation.dialogBuildGuestsInClosurePeriods.errorMessage}</div>`;

    return message;
  }

  /**
   * Gestione consolidamento e validazione dei dati
   */
  public handleConsolidateIstatData(
    profile: OwnerIstatProfile,
    consolidateDialog: TemplateRef<unknown> | undefined,
  ): Observable<string | null> {
    return this.openConsolidateIstatDataModal().pipe(
      filter(
        (
          result,
        ): result is OverlayResult<ConsolidateIstatDataModalResult> & { data: ConsolidateIstatDataModalResult } =>
          result.actionType === ActionType.Confirm && !!result.data,
      ),
      switchMap((result) => {
        const params = new AstatDailyLocalTaxInfoRequest({
          year: result.data.year,
          month: result.data.month,
          ownerIstatId: profile.id,
        });

        return this.dataAccessIstatService.getAstatDailyLocalTaxInfo(params).pipe(
          map((result) => new AstatDailyLocalTaxInfo(result)),
          catchError(() => {
            // Mostra la dialog di errore e blocco il flusso se non ci sono dati ASTAT
            return this.noAstatDataFoundDialog();
          }),
        );
      }),
      switchMap((astatDailyLocalTaxInfo) => {
        if (!astatDailyLocalTaxInfo) {
          // Se non ci sono dati ASTAT, non procedere con il consolidamento
          return of(null);
        }

        // Mostra la dialog di conferma con i dati trovati
        return this.openConsolidateIstatDataDialog(astatDailyLocalTaxInfo, consolidateDialog).pipe(
          switchMap((confirmed) => {
            if (!confirmed) {
              return of(null); // l'utente ha scelto di non consolidare
            }

            const sendAstatDailyIstatClosure = new SendAstatDailyIstatClosureRequest({
              year: astatDailyLocalTaxInfo.year,
              month: astatDailyLocalTaxInfo.month,
              ownerIstatId: profile.id,
            });

            // Invia i dati per il consolidamento
            return this.dataAccessIstatService.sendAstatDailyIstatClosure(sendAstatDailyIstatClosure).pipe(
              tap(() => {
                this.toastService.showSuccess(this.translation.toastSendAstatDailyIstatClosureOk);
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        throw new AppErrorBuilder(`[FeatureIstatService] Error sending astat daily facility closure`, Severity.ERROR)
          .addCause(error)
          .build();
      }),
    );
  }

  /**
   * Apre modale per impostare anno e mese per il consolidamento e validazione dei dati
   */
  private openConsolidateIstatDataModal(): Observable<OverlayResult<ConsolidateIstatDataModalResult>> {
    const lazyComponent = (): Promise<LazyComponent<ModalComponent>> =>
      import('./feature-istat/components/istat-filter/components/consolidate-istat-data-modal/consolidate-istat-data-modal.component').then(
        (x) => ({
          component: x.ConsolidateIstatDataModalComponent,
        }),
      );
    return this.modalService.show({
      loadComponent: lazyComponent,
      title: this.translation.consolidateIstatData,
      confirmButtonText: this.translation.confirmButton,
    });
  }

  /**
   * Mostra il dialog di errore per mancanza di dati ASTAT per il mese-anno selezionato.
   */
  private noAstatDataFoundDialog(): Observable<never> {
    const errors = this.translation.dialogConsolidateIstatErrors;

    const content = `
    ${errors.intro}
    <br>
    ${errors.description}
    <br>
    <ul>
      <li>${errors.prevMonth}</li>
      <li>${errors.missingStats}</li>
      <li>${errors.earlyClosure}</li>
    </ul>
    <br>
    ${errors.noMovements}
  `;

    return this.dialogService
      .showDanger({
        title: this.translation.titleWarning,
        content,
        confirmButtonText: $localize`:@@common.button.confirm:Conferma`,
        cancelButtonText: undefined,
      })
      .pipe(switchMap(() => EMPTY));
  }

  /**
   * Mostra il dialog per consolidare i dati Istat e mostrare i movimenti ASTAT
   */
  private openConsolidateIstatDataDialog(
    astatDailyLocalTaxInfo: AstatDailyLocalTaxInfo,
    consolidateDialog: TemplateRef<unknown> | undefined,
  ): Observable<boolean> {
    return this.dialogService
      .showConfirm({
        title: this.translation.titleWarning,
        content: consolidateDialog,
        locals: astatDailyLocalTaxInfo,
        confirmButtonText: $localize`:@@common.boolean.yes:Sì`,
        cancelButtonText: $localize`:@@common.boolean.no:No`,
      })
      .pipe(map((result) => result.actionType === ActionType.Confirm));
  }

  /**
   * Gestisce la logica per il controllo della City Tax.
   * Mostra i dialog necessari e restituisce un Observable che emette true se l'utente ha confermato, false altrimenti.
   */
  public handleCityTaxCheckInformation(info: CityTaxCheckInformation): Observable<boolean> {
    const uncoveredDaysEmpty = info.uncoveredDaysForCityTaxDefinition.length === 0;
    const customers = info.customerInfoWithoutInvoicedCityTax;

    // Caso 1: Nessun giorno scoperto -> procedi direttamente
    if (uncoveredDaysEmpty) {
      return this.checkCustomersWithoutCityTax(customers);
    }

    // Caso 2: Giorni scoperti -> prima dialog
    return this.showUncoveredDaysDialog(info.uncoveredDaysForCityTaxDefinition).pipe(
      switchMap((confirmed) => {
        if (!confirmed) return of(false);
        // Se confermato, controlla clienti senza tassa
        return this.checkCustomersWithoutCityTax(customers);
      }),
    );
  }

  /**
   * Controlla i clienti senza tassa di soggiorno.
   */
  private checkCustomersWithoutCityTax(customers: CustomerInfoWithoutInvoicedCityTax[]): Observable<boolean> {
    if (customers.length === 0) {
      return of(true);
    }
    return this.showCustomersWithoutCityTaxDialog(customers);
  }

  /**
   * Mostra il dialog per i giorni scoperti.
   */
  private showUncoveredDaysDialog(days: DateTime[]): Observable<boolean> {
    const formattedDays = days.map((d) => d.toFormat('dd/MM/yyyy')).join(', ');
    return this.dialogService
      .showConfirm({
        title: this.translation.dialogUncoveredDaysTitle,
        content: this.translation.dialogUncoveredDaysContent(formattedDays),
      })
      .pipe(map((result) => result.actionType === ActionType.Confirm));
  }

  /**
   * Mostra il dialog per i clienti senza tassa di soggiorno.
   */
  private showCustomersWithoutCityTaxDialog(customers: CustomerInfoWithoutInvoicedCityTax[]): Observable<boolean> {
    const customerList = customers.map((c) => `${c.surname} ${c.name ?? ''}`.trim()).join('<br>');

    return this.dialogService
      .showConfirm({
        title: this.translation.dialogCustomersWithoutCityTaxTitle,
        content: this.translation.dialogCustomersWithoutCityTaxContent(customerList),
        confirmButtonText: $localize`:@@common.button.continue:Continua`,
        cancelButtonText: $localize`:@@common.button.cancel:Annulla`,
      })
      .pipe(map((result) => result.actionType === ActionType.Confirm));
  }
}
